import React, { useState, useEffect } from 'react';
import { Music, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { checkAppleMusicAvailability, handleMusicKitError, formatErrorForToast } from '../utils/appleMusicErrors';

const AppleMusicAuth = ({ onAuthenticated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('[AppleMusicAuth] Component mounted, loading MusicKit JS...');
    // Load MusicKit JS
    const script = document.createElement('script');
    script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    script.async = true;
    script.onload = () => {
      console.log('[AppleMusicAuth] MusicKit JS loaded successfully');
      configureMusicKit();
    };
    script.onerror = (error) => {
      console.error('[AppleMusicAuth] Failed to load MusicKit JS:', error);
      setError({
        title: 'Loading Error',
        message: 'Failed to load Apple Music library',
        action: 'Retry'
      });
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const configureMusicKit = async () => {
    try {
      console.log('[AppleMusicAuth] Starting MusicKit configuration...');
      console.log('[AppleMusicAuth] window.MusicKit available?', !!window.MusicKit);
      
      // Skip availability check before configuration since MusicKit isn't configured yet
      
      // Get developer token from server (respect API base URL)
      const base = process.env.REACT_APP_API_URL || '';
      const tokenUrl = `${base}/api/auth/apple/token`;
      console.log('[AppleMusicAuth] Fetching developer token from:', tokenUrl);
      
      const response = await fetch(tokenUrl, { 
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AppleMusicAuth] Token fetch failed:', response.status, errorText);
        throw new Error(`Failed to get token: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[AppleMusicAuth] Token response:', data);
      const { developerToken } = data;
      
      if (!developerToken) {
        throw new Error('No developer token received from server');
      }
      
      console.log('[AppleMusicAuth] Developer token received, length:', developerToken.length);

      console.log('[AppleMusicAuth] Configuring MusicKit...');
      
      // Check if MusicKit is already configured
      try {
        const existingInstance = window.MusicKit.getInstance();
        if (existingInstance) {
          console.log('[AppleMusicAuth] MusicKit already configured, using existing instance');
          setIsConfigured(true);
          setError(null);
          
          if (existingInstance.isAuthorized) {
            console.log('[AppleMusicAuth] User already authorized');
            setIsAuthorized(true);
            onAuthenticated(existingInstance.musicUserToken);
          }
          return;
        }
      } catch (error) {
        console.log('[AppleMusicAuth] No existing instance found, proceeding with configuration');
      }
      
      window.MusicKit.configure({
        developerToken,
        app: {
          name: 'JukeHive',
          build: '1.0.0'
        },
        storefrontId: 'us' // Default to US storefront
      });
      console.log('[AppleMusicAuth] MusicKit configured successfully');

      setIsConfigured(true);
      setError(null);
      
      // Try to get instance after a small delay to ensure configuration is complete
      setTimeout(async () => {
        try {
          const music = window.MusicKit.getInstance();
          console.log('[AppleMusicAuth] MusicKit instance obtained:', !!music);
          
          // Initialize the player if it doesn't exist
          if (music && !music.player) {
            console.log('[AppleMusicAuth] Initializing MusicKit player...');
            try {
              // Some versions of MusicKit need explicit player initialization
              if (music.play && typeof music.play === 'function') {
                // The player is initialized when first trying to play
                // We'll just prepare it without actually playing
                music.volume = 0.5; // Set default volume
                console.log('[AppleMusicAuth] Player prepared');
              }
            } catch (playerError) {
              console.log('[AppleMusicAuth] Player initialization note:', playerError.message);
            }
          }
          
          if (music && music.isAuthorized) {
            console.log('[AppleMusicAuth] User already authorized');
            setIsAuthorized(true);
            onAuthenticated(music.musicUserToken);
          }
          
          // Update availability after configuration
          checkAppleMusicAvailability().then(postConfigCheck => {
            console.log('[AppleMusicAuth] Post-config availability:', postConfigCheck);
            setAvailability(postConfigCheck);
          });
        } catch (instanceError) {
          console.log('[AppleMusicAuth] Could not get instance yet, will try on user action');
        }
      }, 100);
    } catch (error) {
      console.error('Failed to configure MusicKit:', error);
      const errorInfo = handleMusicKitError(error);
      setError(errorInfo);
      toast.error(formatErrorForToast(error));
    }
  };

  const handleAuthorize = async () => {
    if (!isConfigured) {
      toast.error('Apple Music is not configured yet');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[AppleMusicAuth] Starting authorization...');
      const music = window.MusicKit.getInstance();
      console.log('[AppleMusicAuth] Got instance, authorizing...');
      console.log('[AppleMusicAuth] Music instance properties:', Object.keys(music));
      
      await music.authorize();
      console.log('[AppleMusicAuth] Authorization successful');
      
      // After authorization, check player status
      console.log('[AppleMusicAuth] Post-auth player check:', {
        hasPlayer: !!music.player,
        hasPlay: typeof music.play === 'function',
        hasQueue: !!music.queue,
        isAuthorized: music.isAuthorized
      });
      
      // Initialize player if needed
      if (!music.player && music.play) {
        console.log('[AppleMusicAuth] Initializing player after authorization...');
        music.volume = 0.5;
      }
      
      setIsAuthorized(true);
      onAuthenticated(music.musicUserToken);
      toast.success('Connected to Apple Music!');
      
      // Update availability after authorization
      const postAuthCheck = await checkAppleMusicAvailability();
      setAvailability(postAuthCheck);
    } catch (error) {
      console.error('Authorization failed:', error);
      const errorInfo = handleMusicKitError(error);
      setError(errorInfo);
      toast.error(formatErrorForToast(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthorized) {
    return (
      <div className="bg-green-600/20 border border-green-500 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-400 font-medium">Connected to Apple Music</span>
          </div>
        </div>
        {availability?.capabilities && (
          <div className="text-xs text-green-300 mt-2 space-y-1">
            {availability.capabilities.hasSubscription && (
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-3 h-3" />
                <span>Active subscription</span>
              </div>
            )}
            {availability.capabilities.canPlayCatalog && (
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-3 h-3" />
                <span>Can play full songs</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
      <div className="flex items-center space-x-3 mb-4">
        <Music className="w-6 h-6 text-pink-400" />
        <h3 className="text-lg font-semibold text-white">Connect Apple Music</h3>
      </div>
      
      {error ? (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">{error.title}</p>
              <p className="text-red-200 text-sm mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-300 mb-4">
          Connect your Apple Music account to search and suggest songs
        </p>
      )}
      
      {!isConfigured && !error && (
        <div className="flex items-center justify-center py-4 text-gray-400">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          <span>Initializing...</span>
        </div>
      )}
      
      {(isConfigured || error?.action === 'Sign In') && (
        <button
          onClick={handleAuthorize}
          disabled={!isConfigured || isLoading}
          className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Connecting...</span>
            </>
          ) : (
            <>
              <Music className="w-4 h-4" />
              <span>{error?.action === 'Sign In' ? 'Try Again' : 'Connect Apple Music'}</span>
            </>
          )}
        </button>
      )}
      
      {availability && !availability.available && availability.reason === 'HTTPS required' && (
        <p className="text-yellow-400 text-xs mt-3">
          ⚠️ Apple Music requires HTTPS. Please access this site over a secure connection.
        </p>
      )}
    </div>
  );
};

export default AppleMusicAuth;